/** @format */

import { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { errorToast, successToast } from 'components/common/Toast/Toast';
import form from '../authForm.module.css';
import AuthField from '../AuthField/AuthField';
import PostalCodeFields from '../PostalCodeFields/PostalCodeFields';
import contactValidationSchema from 'validation/Registration/ContactSchema';

const ACCOUNT_FIELDS = ['username', 'contactEmail', 'emailConfirm', 'password', 'passwordConfirm'];

const ContactForm = ({
  contactData,
  setContactData,
  step,
  setStep,
  handleNext,
  setSubmitted,
  wantShop,
  setWantShop,
  setOpen,
  newsAccept,
  setNewsAccept,
  subscribeNewsletter
}) => {
  const { t } = useTranslation();
  const formikRef = useRef(null);
  const [initialValues] = useState(() => ({
    username: '',
    password: '',
    passwordConfirm: '',
    emailConfirm: '',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    contactCountry: 'Magyarország',
    contactCity: '',
    contactZipCode: '',
    contactStreetAndNumber: '',
    ...contactData
  }));

  // Zip code and city are edited through PostalCodeFields, which only writes to contactData.
  useEffect(() => {
    formikRef.current.setFieldValue('contactCity', contactData.contactCity);
    formikRef.current.setFieldValue('contactZipCode', contactData.contactZipCode);
  }, [contactData.contactCity, contactData.contactZipCode]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={contactValidationSchema(t)}
      onSubmit={async (values, { setSubmitting }) => {
        if (newsAccept && !wantShop) {
          let value = values;
          await subscribeNewsletter({ value }).then((res) => {
            if (res) {
              successToast(t('billingForm.newsletter.success'));
              setSubmitting(true);
              setContactData(values);
              wantShop ? handleNext() : setOpen(true);
              setSubmitted(true);
              setSubmitting(false);
            } else {
              errorToast(t('billingForm.newsletter.error'));
            }
          });
        } else {
          setSubmitting(true);
          setContactData(values);
          wantShop ? handleNext() : setOpen(true);
          setSubmitted(true);
          setSubmitting(false);
        }
      }}>
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        validateForm,
        setTouched,
        setFieldTouched,
        submitForm
      }) => {
        const bind = (name) => ({
          id: name,
          name,
          value: values[name],
          onChange: (e) => {
            handleChange(e);
            setContactData({ ...contactData, [name]: e.target.value });
          },
          onBlur: handleBlur,
          error: touched[name] && errors[name]
        });

        // Only the fields of the account step may block leaving it; the rest is validated on submit.
        const hasAccountErrors = async () => {
          const formErrors = await validateForm();
          if (!ACCOUNT_FIELDS.some((field) => formErrors[field])) {
            return false;
          }
          setTouched({ ...touched, ...Object.fromEntries(ACCOUNT_FIELDS.map((field) => [field, true])) }, false);
          return true;
        };

        const handleFormSubmit = async (e) => {
          e.preventDefault();
          if (await hasAccountErrors()) {
            setStep(0);
          } else if (step === 0) {
            setStep(1);
          } else {
            submitForm();
          }
        };

        return (
          <form className={form.form} onSubmit={handleFormSubmit} noValidate>
            {step === 0 && (
              <>
                <AuthField {...bind('username')} label={t('common.username')} autoComplete='username' autoFocus />
                <AuthField {...bind('contactEmail')} label={t('common.email')} type='email' autoComplete='email' />
                <AuthField
                  {...bind('emailConfirm')}
                  label={t('common.emailConfirm')}
                  type='email'
                  autoComplete='email'
                />
                <div className={form.row}>
                  <AuthField
                    {...bind('password')}
                    label={t('common.password')}
                    type='password'
                    autoComplete='new-password'
                    onFocus={() => setFieldTouched('password', true)}
                  />
                  <AuthField
                    {...bind('passwordConfirm')}
                    label={t('common.confirmPassword')}
                    type='password'
                    autoComplete='new-password'
                  />
                </div>
                <div className={form.actions}>
                  <button type='submit' className={`${form.button} ${form.buttonPrimary}`}>
                    {t('common.next')}
                  </button>
                </div>
              </>
            )}
            {step === 1 && (
              <>
                <div className={form.row}>
                  <AuthField
                    {...bind('contactFirstName')}
                    label={t('common.lastName')}
                    autoComplete='family-name'
                    autoFocus
                  />
                  <AuthField {...bind('contactLastName')} label={t('common.firstName')} autoComplete='given-name' />
                </div>
                <AuthField
                  {...bind('contactPhone')}
                  label={t('common.phone')}
                  type='tel'
                  prefix='+36'
                  placeholder='301234567'
                  autoComplete='tel-national'
                />
                <div className={`${form.row} ${form.rowZipCity}`}>
                  <PostalCodeFields
                    data={contactData}
                    setData={setContactData}
                    zipHelper={touched.contactZipCode}
                    zipError={errors.contactZipCode}
                    cityHelper={touched.contactCity}
                    cityError={errors.contactCity}
                  />
                </div>
                <div className={`${form.row} ${form.rowStreetCountry}`}>
                  <AuthField
                    {...bind('contactStreetAndNumber')}
                    label={t('billingForm.streetAndNumber')}
                    autoComplete='address-line1'
                  />
                  <AuthField {...bind('contactCountry')} label={t('billingForm.country')} disabled />
                </div>
                <div className={form.options}>
                  <div className={form.option}>
                    <label className={form.checkbox}>
                      <input
                        type='checkbox'
                        name='wantShop'
                        checked={wantShop}
                        onChange={() => setWantShop(!wantShop)}
                        aria-describedby='wantShop-hint'
                      />
                      <span>{t('register.wantShop')}</span>
                    </label>
                    <InfoIcon className={form.info} aria-hidden='true' />
                    <p id='wantShop-hint' className={form.hint}>
                      {t('contactForm.wantShop')}
                    </p>
                  </div>
                  <div className={form.option}>
                    <label className={form.checkbox}>
                      <input
                        type='checkbox'
                        name='newsAccept'
                        checked={newsAccept}
                        onChange={(e) => setNewsAccept(e.target.checked)}
                        aria-describedby='newsAccept-hint'
                      />
                      <span>{t('billingForm.newsletter.subscribe')}</span>
                    </label>
                    <InfoIcon className={form.info} aria-hidden='true' />
                    <p id='newsAccept-hint' className={form.hint}>
                      {t('billingForm.newsletter.title')}
                    </p>
                  </div>
                </div>
                <div className={form.actions}>
                  <button type='button' className={`${form.button} ${form.buttonGhost}`} onClick={() => setStep(0)}>
                    {t('common.back')}
                  </button>
                  <button type='submit' className={`${form.button} ${form.buttonPrimary}`}>
                    {wantShop ? t('common.next') : t('common.register')}
                  </button>
                </div>
              </>
            )}
          </form>
        );
      }}
    </Formik>
  );
};

export default ContactForm;
